import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useResizableColumns } from "@/hooks/use-resizable-columns";
import { useColumnOrder } from "@/hooks/use-column-order";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Checkbox } from "@/components/ui/checkbox";
import { SpreadsheetCell } from "./spreadsheet-cell";
import {
  XCircle, Star, Calendar, Plus, Loader2, Settings, Trash2, Pencil,
  Check, X, Undo2, Redo2, ChevronDown, Mail, Phone, Bell, Eye,
  GripVertical, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Clock,
  RotateCcw, Edit, Send,
} from "lucide-react";
import { apiRequest, queryClient as globalQc } from "@/lib/queryClient";
import { ScheduledEmailDialog } from "@/components/scheduled-email-dialog";
import { useToast } from "@/hooks/use-toast";
import type { PlatformRegion } from "@shared/schema";

// ─── Types ────────────────────────────────────────────────────────────────────

type Prospect = {
  id: string; recordId?: string | null; name: string; email: string;
  phone?: string | null; company?: string | null; contactName?: string | null;
  businessType?: string | null; website?: string | null; address?: string | null;
  city?: string | null; state?: string | null; zipCode?: string | null;
  region?: string | null; rating?: number | null; rep?: string | null;
  lastContacted?: string | null; lastActivityAt?: string | null;
  emailOptedOut: boolean; starred: boolean; notes?: string | null; status: string;
  source: string; convertedAt?: string | null; createdAt: string; updatedAt: string;
};

type ProspectActivity = {
  id: string; prospectId: string; type: "call" | "email" | "note";
  notes?: string | null; createdAt: string;
};

type UndoAction =
  | { type: "edit"; prospectId: string; field: string; oldValue: any; newValue: any }
  | { type: "delete"; prospect: Prospect };

// ─── Column definitions ────────────────────────────────────────────────────────

const getColumns = (businessTypes: string[]) => [
  { key: "starred", label: "★", width: "52px", type: "starred" },
  { key: "name", label: "Account Name", width: "180px" },
  { key: "phone", label: "Phone Number", width: "140px", type: "phone" },
  { key: "email", label: "Email Address", width: "200px" },
  { key: "contactName", label: "Contact Name", width: "160px" },
  { key: "lastContacted", label: "Last Contacted", width: "140px", type: "date" },
  { key: "notes", label: "Notes", width: "250px" },
  { key: "region", label: "Region", width: "150px", type: "region" },
  { key: "businessType", label: "Business Type", width: "150px", type: "select", options: businessTypes },
  { key: "rep", label: "Rep", width: "130px" },
  { key: "website", label: "Website", width: "200px", type: "url" },
  { key: "emailOptedOut", label: "Opt Out", width: "80px", type: "checkbox" },
];

const SERVER_SORTABLE = new Set(["name", "email", "lastContacted", "region", "businessType", "rep", "createdAt"]);

// ─── Log Call dialog ──────────────────────────────────────────────────────────

function LogCallDialog({ open, onOpenChange, title, onSave, isPending }: {
  open: boolean; onOpenChange: (v: boolean) => void; title: string;
  onSave: (notes: string) => void; isPending: boolean;
}) {
  const [notes, setNotes] = useState("");
  useEffect(() => { if (!open) setNotes(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <VoiceToTextTextarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Call notes…"
            rows={5}
            data-testid="textarea-log-call-notes"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(notes)} disabled={!notes.trim() || isPending} data-testid="button-save-log-call">
            {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function ProspectsSpreadsheet({ searchQuery = "" }: { searchQuery?: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();

  // ── Filter state ──────────────────────────────────────────────────────────────
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [selectedBusinessType, setSelectedBusinessType] = useState<string>("all");
  const [regionSearch, setRegionSearch] = useState("");
  const [isRegionDialogOpen, setIsRegionDialogOpen] = useState(false);
  const [isManageRegionsOpen, setIsManageRegionsOpen] = useState(false);
  const [manageRegionSearch, setManageRegionSearch] = useState("");
  const [newRegionName, setNewRegionName] = useState("");
  const [regionNameError, setRegionNameError] = useState<string | null>(null);
  const [editingRegionId, setEditingRegionId] = useState<string | null>(null);
  const [editingRegionName, setEditingRegionName] = useState("");
  const [regionDeleteError, setRegionDeleteError] = useState<{
    regionId: string; regionName: string; message: string; prospectCount: number;
  } | null>(null);
  const [reassignTargetRegionId, setReassignTargetRegionId] = useState<string>("");

  // ── Spreadsheet state ─────────────────────────────────────────────────────────
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{ rowId: string; column: string } | null>(null);
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [redoStack, setRedoStack] = useState<UndoAction[]>([]);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [visibleRowCount, setVisibleRowCount] = useState(50);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [bulkEditField, setBulkEditField] = useState("");
  const [bulkEditValue, setBulkEditValue] = useState("");

  // ── Action toolbar state ──────────────────────────────────────────────────────
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isProspectEmailScheduleOpen, setIsProspectEmailScheduleOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [isLogCallOpen, setIsLogCallOpen] = useState(false);
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [notesProspectId, setNotesProspectId] = useState<string | null>(null);
  const [notesValue, setNotesValue] = useState("");

  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Data ──────────────────────────────────────────────────────────────────────
  const { data: regions = [] } = useQuery<PlatformRegion[]>({
    queryKey: ["/api/platform/regions"],
  });

  const { data: platformBizTypes = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/platform/business-types"],
  });
  const businessTypes = platformBizTypes.map((t) => t.name);

  // ── Column resize / order ─────────────────────────────────────────────────────
  const COLS = useMemo(() => getColumns(businessTypes), [businessTypes]);
  const colKeys = useMemo(() => COLS.map((c) => c.key), [COLS]);
  const { getColumnWidth, handleResizeStart } = useResizableColumns({
    columns: COLS,
    storageKey: "prospects-spreadsheet-column-widths",
  });
  const { orderedKeys, moveColumn } = useColumnOrder({
    columnKeys: colKeys,
    storageKey: "prospects-spreadsheet-column-order",
  });
  const COLUMNS = useMemo(() => {
    const map = new Map(COLS.map((c) => [c.key, c] as const));
    return orderedKeys.map((k) => map.get(k)!).filter(Boolean);
  }, [orderedKeys, COLS]);
  const [draggedColKey, setDraggedColKey] = useState<string | null>(null);
  const [dragOverColKey, setDragOverColKey] = useState<string | null>(null);

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const prospectsQk = useMemo(() => {
    const p: Record<string, string> = {};
    if (debouncedSearch) p.q = debouncedSearch;
    if (selectedRegion !== "all") p.region = selectedRegion;
    if (selectedBusinessType !== "all") p.businessType = selectedBusinessType;
    return ["/api/platform/prospects", p] as const;
  }, [debouncedSearch, selectedRegion, selectedBusinessType]);

  const { data: prospects = [], isLoading, isError, refetch } = useQuery<Prospect[]>({
    queryKey: prospectsQk,
    queryFn: async () => {
      const url = new URL("/api/platform/prospects", window.location.origin);
      const [, params] = prospectsQk;
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
      const r = await fetch(url.toString(), { credentials: "include" });
      if (!r.ok) throw new Error("Failed");
      return r.json();
    },
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["/api/platform/prospects"] });
  }, [qc]);

  // ── Sorting ───────────────────────────────────────────────────────────────────
  const sortedProspects = useMemo(() => {
    if (!sortField) return prospects;
    return [...prospects].sort((a, b) => {
      const av = ((a as any)[sortField] ?? "") as string;
      const bv = ((b as any)[sortField] ?? "") as string;
      const cmp = av.localeCompare(bv, undefined, { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [prospects, sortField, sortDir]);

  const emptyRows = useMemo(() => {
    const n = Math.max(0, visibleRowCount - sortedProspects.length);
    return Array.from({ length: n }, (_, i) => ({ id: `empty-${i}`, isEmpty: true }));
  }, [sortedProspects.length, visibleRowCount]);

  // ── Virtualizer ───────────────────────────────────────────────────────────────
  const rowVirtualizer = useVirtualizer({
    count: sortedProspects.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 28,
    overscan: 10,
  });

  // ── Region helpers ────────────────────────────────────────────────────────────
  const filteredRegions = useMemo(
    () => regions.filter((r) => r.name.toLowerCase().includes(regionSearch.toLowerCase())),
    [regions, regionSearch],
  );
  const filteredManageRegions = useMemo(
    () => regions.filter((r) => r.name.toLowerCase().includes(manageRegionSearch.toLowerCase())),
    [regions, manageRegionSearch],
  );
  const validateRegionFormat = (name: string): string | null => {
    const t = name.trim();
    if (!t) return "Region name is required";
    if (t.length < 2) return "Region name must be at least 2 characters";
    if (t.length > 100) return "Region name must be 100 characters or less";
    return null;
  };

  // ── Mutations ─────────────────────────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: object }) =>
      apiRequest("PATCH", `/api/platform/prospects/${id}`, updates),
    onSuccess: () => invalidate(),
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/prospects/${id}`),
    onSuccess: () => {
      invalidate();
      setSelectedRowId(null);
      toast({ title: "Prospect deleted" });
    },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  const createMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/platform/prospects", {
      name: "",
      email: "placeholder@example.com",
      region: selectedRegion !== "all" ? selectedRegion : (regions[0]?.name ?? ""),
      businessType: selectedBusinessType !== "all" ? selectedBusinessType : "",
      source: "manual",
    }),
    onSuccess: async (res) => {
      invalidate();
      const p = await res.json();
      setSelectedRowId(p.id);
      setEditingCell({ rowId: p.id, column: "name" });
    },
    onError: () => toast({ title: "Failed to create prospect", variant: "destructive" }),
  });

  const convertMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/platform/prospects/${id}/convert`, {}),
    onSuccess: () => {
      invalidate();
      setSelectedRowId(null);
      toast({ title: "Prospect converted" });
    },
    onError: () => toast({ title: "Failed to convert", variant: "destructive" }),
  });

  const activityMutation = useMutation({
    mutationFn: ({ id, type, notes }: { id: string; type: string; notes: string }) =>
      apiRequest("POST", `/api/platform/prospects/${id}/activities`, { type, notes }),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["/api/platform/prospects", selectedRowId, "activities"] });
      setIsLogCallOpen(false);
      toast({ title: "Activity logged" });
    },
    onError: () => toast({ title: "Failed to log activity", variant: "destructive" }),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiRequest("POST", "/api/platform/prospects/bulk-delete", { ids }),
    onSuccess: () => {
      invalidate();
      setSelectedIds(new Set());
      setIsBulkDeleteOpen(false);
      toast({ title: "Prospects deleted" });
    },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  const bulkEditMutation = useMutation({
    mutationFn: ({ ids, fields }: { ids: string[]; fields: Record<string, string> }) =>
      apiRequest("POST", "/api/platform/prospects/bulk-edit", { ids, fields }),
    onSuccess: () => {
      invalidate();
      setSelectedIds(new Set());
      setIsBulkEditOpen(false);
      toast({ title: "Updated" });
    },
    onError: () => toast({ title: "Failed to update", variant: "destructive" }),
  });

  // Region mutations
  const createRegionMutation = useMutation({
    mutationFn: (name: string) => apiRequest("POST", "/api/platform/regions", { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/regions"] });
      setNewRegionName("");
      setIsRegionDialogOpen(false);
      toast({ title: "Region created" });
    },
    onError: async (err: any) => {
      const body = err?.response ? await err.response.json().catch(() => ({})) : {};
      toast({ title: body?.message || "Failed to create region", variant: "destructive" });
    },
  });

  const updateRegionMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiRequest("PATCH", `/api/platform/regions/${id}`, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/regions"] });
      setEditingRegionId(null);
      setEditingRegionName("");
    },
    onError: () => toast({ title: "Failed to update region", variant: "destructive" }),
  });

  const deleteRegionMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const r = await apiRequest("DELETE", `/api/platform/regions/${id}`);
      if (!r.ok) {
        const data = await r.json();
        throw { ...data, regionId: id, regionName: name };
      }
      return { id, name };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/platform/regions"] });
      if (selectedRegion === data.name) setSelectedRegion("all");
      toast({ title: "Region deleted" });
    },
    onError: (err: any) => {
      if (err?.hasAssociatedRecords) {
        setRegionDeleteError({
          regionId: err.regionId,
          regionName: err.regionName,
          message: err.message,
          prospectCount: err.prospectCount || 0,
        });
        setReassignTargetRegionId("");
      } else {
        toast({ title: err?.message || "Failed to delete region", variant: "destructive" });
      }
    },
  });

  const deleteRegionWithReassignMutation = useMutation({
    mutationFn: ({ regionId, reassignToRegionId }: { regionId: string; reassignToRegionId: string }) =>
      apiRequest("POST", `/api/platform/regions/${regionId}/delete-with-reassignment`, { reassignToRegionId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/regions"] });
      invalidate();
      setRegionDeleteError(null);
      setReassignTargetRegionId("");
      toast({ title: "Region deleted and prospects reassigned" });
    },
    onError: () => toast({ title: "Failed to delete region", variant: "destructive" }),
  });

  // ── Cell handling ─────────────────────────────────────────────────────────────
  const handleCellChange = (prospectId: string, column: string, value: any) => {
    if (prospectId.startsWith("empty-")) {
      createMutation.mutate();
      return;
    }
    const prospect = prospects.find((p) => p.id === prospectId);
    if (prospect) {
      const oldValue = (prospect as any)[column];
      setUndoStack((prev) => [...prev, { type: "edit", prospectId, field: column, oldValue, newValue: value }]);
      setRedoStack([]);
    }
    updateMutation.mutate({ id: prospectId, updates: { [column]: value } });
  };

  const handleUndo = async () => {
    if (!undoStack.length) return;
    const action = undoStack[undoStack.length - 1];
    setUndoStack((p) => p.slice(0, -1));
    if (action.type === "edit") {
      await apiRequest("PATCH", `/api/platform/prospects/${action.prospectId}`, { [action.field]: action.oldValue });
      setRedoStack((p) => [...p, action]);
      invalidate();
      toast({ title: "Undone" });
    } else if (action.type === "delete") {
      const { id, ...data } = action.prospect;
      await apiRequest("POST", "/api/platform/prospects", data);
      setRedoStack((p) => [...p, action]);
      invalidate();
      toast({ title: "Undone — prospect restored" });
    }
  };

  const handleRedo = async () => {
    if (!redoStack.length) return;
    const action = redoStack[redoStack.length - 1];
    setRedoStack((p) => p.slice(0, -1));
    if (action.type === "edit") {
      await apiRequest("PATCH", `/api/platform/prospects/${action.prospectId}`, { [action.field]: action.newValue });
      setUndoStack((p) => [...p, action]);
      invalidate();
      toast({ title: "Redone" });
    } else if (action.type === "delete") {
      await apiRequest("DELETE", `/api/platform/prospects/${action.prospect.id}`);
      setUndoStack((p) => [...p, action]);
      invalidate();
      toast({ title: "Redone — prospect deleted again" });
    }
  };

  const handleDeleteWithUndo = (id: string) => {
    const prospect = prospects.find((p) => p.id === id);
    if (prospect) {
      setUndoStack((prev) => [...prev, { type: "delete", prospect }]);
      setRedoStack([]);
    }
    deleteMutation.mutate(id);
  };

  const handleTabNavigation = (prospectId: string, colKey: string, shiftKey: boolean) => {
    const editableCols = COLUMNS.filter((c) => c.type !== "region" && c.type !== "select" && c.type !== "checkbox" && c.type !== "starred");
    const colIdx = editableCols.findIndex((c) => c.key === colKey);
    const rowIdx = sortedProspects.findIndex((p) => p.id === prospectId);
    if (shiftKey) {
      if (colIdx > 0) { setEditingCell({ rowId: prospectId, column: editableCols[colIdx - 1].key }); }
      else if (rowIdx > 0) { setEditingCell({ rowId: sortedProspects[rowIdx - 1].id, column: editableCols[editableCols.length - 1].key }); }
    } else {
      if (colIdx < editableCols.length - 1) { setEditingCell({ rowId: prospectId, column: editableCols[colIdx + 1].key }); }
      else if (rowIdx < sortedProspects.length - 1) { setEditingCell({ rowId: sortedProspects[rowIdx + 1].id, column: editableCols[0].key }); }
    }
  };

  const toggleSort = (field: string) => {
    if (!SERVER_SORTABLE.has(field)) return;
    if (sortField === field) {
      if (sortDir === "asc") setSortDir("desc");
      else { setSortField(null); setSortDir("asc"); }
    } else { setSortField(field); setSortDir("asc"); }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (!SERVER_SORTABLE.has(field)) return null;
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const selectedProspect = selectedRowId ? prospects.find((p) => p.id === selectedRowId) : null;

  // ── Activity timeline query ───────────────────────────────────────────────────
  const { data: activities = [] } = useQuery<ProspectActivity[]>({
    queryKey: ["/api/platform/prospects", selectedRowId, "activities"],
    queryFn: async () => {
      if (!selectedRowId) return [];
      const r = await fetch(`/api/platform/prospects/${selectedRowId}/activities`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!selectedRowId && isActivityOpen,
  });

  const getRowClassName = (p: Prospect | { id: string; isEmpty: boolean }) => {
    if ("isEmpty" in p) return "";
    if (selectedRowId === p.id) return "spreadsheet-row-selected";
    if (p.status === "converted") return "spreadsheet-row-booked";
    if (p.status === "interested") return "spreadsheet-row-interested";
    if (p.status === "not_now") return "spreadsheet-row-not-now";
    if (p.status === "disqualified") return "spreadsheet-row-disqualified";
    return "";
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">

      {/* ── Toolbar row: Region / Business Type / Undo-Redo ──────────────────── */}
      <div className="border-b border-border bg-card overflow-x-auto">
        <div className="flex items-center gap-3 p-4 min-w-max">

          {/* Region filter */}
          <Label className="text-sm font-medium">Region:</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-48 justify-between" data-testid="select-region">
                <span className="truncate">
                  {selectedRegion === "all" ? "All Regions" : selectedRegion}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="start">
              <div className="p-2 border-b">
                <Input
                  placeholder="Search regions…"
                  value={regionSearch}
                  onChange={(e) => setRegionSearch(e.target.value)}
                  className="h-8"
                  data-testid="input-region-search"
                />
              </div>
              <div className="max-h-60 overflow-auto p-2 space-y-1">
                <div
                  className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                  onClick={() => setSelectedRegion("all")}
                >
                  <Checkbox
                    checked={selectedRegion === "all"}
                    onCheckedChange={() => setSelectedRegion("all")}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="checkbox-all-regions"
                  />
                  <span className="text-sm font-medium">All Regions</span>
                </div>
                {filteredRegions.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                    onClick={() => setSelectedRegion(selectedRegion === r.name ? "all" : r.name)}
                  >
                    <Checkbox
                      checked={selectedRegion === r.name}
                      onCheckedChange={(checked) => setSelectedRegion(checked ? r.name : "all")}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`checkbox-region-${r.id}`}
                    />
                    <span className="text-sm">{r.name}</span>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* New Region */}
          <Dialog open={isRegionDialogOpen} onOpenChange={setIsRegionDialogOpen}>
            <Button variant="outline" size="sm" onClick={() => setIsRegionDialogOpen(true)} data-testid="button-add-region">
              <Plus className="w-4 h-4 mr-1" /> New Region
            </Button>
            <DialogContent>
              <DialogHeader><DialogTitle>Create New Region</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="regionName">Region Name</Label>
                  <Input
                    id="regionName"
                    value={newRegionName}
                    onChange={(e) => { setNewRegionName(e.target.value); setRegionNameError(null); }}
                    placeholder="e.g., Columbus, OH"
                    data-testid="input-region-name"
                  />
                  {regionNameError && <p className="text-xs text-destructive">{regionNameError}</p>}
                </div>
                <Button
                  className="w-full"
                  onClick={() => {
                    const err = validateRegionFormat(newRegionName);
                    if (err) { setRegionNameError(err); return; }
                    createRegionMutation.mutate(newRegionName.trim());
                  }}
                  disabled={!newRegionName.trim() || createRegionMutation.isPending}
                  data-testid="button-create-region"
                >
                  {createRegionMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create Region
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Manage Regions */}
          <Dialog
            open={isManageRegionsOpen}
            onOpenChange={(open) => { setIsManageRegionsOpen(open); if (!open) setManageRegionSearch(""); }}
          >
            <Button variant="outline" size="sm" onClick={() => setIsManageRegionsOpen(true)} data-testid="button-manage-regions">
              <Settings className="w-4 h-4 mr-1" /> Manage Regions
            </Button>
            <DialogContent>
              <DialogHeader><DialogTitle>Manage Regions</DialogTitle></DialogHeader>
              <div className="pb-2">
                <Input
                  placeholder="Search regions…"
                  value={manageRegionSearch}
                  onChange={(e) => setManageRegionSearch(e.target.value)}
                  className="h-9"
                  data-testid="input-manage-regions-search"
                />
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {regions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No regions yet</p>
                ) : filteredManageRegions.map((region) => (
                  <div key={region.id} className="flex items-center gap-2 p-2 rounded-md border border-border">
                    {editingRegionId === region.id ? (
                      <>
                        <Input
                          value={editingRegionName}
                          onChange={(e) => setEditingRegionName(e.target.value)}
                          className="h-8 flex-1"
                          autoFocus
                          data-testid={`input-edit-region-${region.id}`}
                        />
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => updateRegionMutation.mutate({ id: region.id, name: editingRegionName })}
                          disabled={!editingRegionName.trim() || updateRegionMutation.isPending}
                          data-testid={`button-save-region-${region.id}`}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => { setEditingRegionId(null); setEditingRegionName(""); }}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm">{region.name}</span>
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => { setEditingRegionId(region.id); setEditingRegionName(region.name); }}
                          data-testid={`button-edit-region-${region.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => deleteRegionMutation.mutate({ id: region.id, name: region.name })}
                          data-testid={`button-delete-region-${region.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>

          {/* Business Type filter */}
          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-border">
            <Label className="text-sm font-medium">Business Type:</Label>
            <Select value={selectedBusinessType} onValueChange={setSelectedBusinessType}>
              <SelectTrigger className="w-48" data-testid="select-business-type-filter">
                <SelectValue placeholder="All Business Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Business Types</SelectItem>
                {businessTypes.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Undo / Redo */}
          <div className="flex items-center gap-1 ml-2">
            <Button variant="ghost" size="icon" onClick={handleUndo} disabled={!undoStack.length} className="h-8 w-8" title="Undo" data-testid="button-undo">
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleRedo} disabled={!redoStack.length} className="h-8 w-8" title="Redo" data-testid="button-redo">
              <Redo2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Action toolbar: Email / Log Call | Set Reminder / Activity / Prospect Actions / Add Prospect ── */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-2 border-b border-border bg-muted/50">
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => {
              if (selectedProspect) { setEmailTo(selectedProspect.email); setEmailSubject(""); setEmailBody(""); setIsEmailOpen(true); }
            }}
            disabled={!selectedProspect?.email}
            data-testid="button-email-prospect"
          >
            <Mail className="w-4 h-4 mr-1" /> Email
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => setIsLogCallOpen(true)}
            disabled={!selectedRowId || selectedRowId.startsWith("empty-")}
            data-testid="button-log-call"
          >
            <Phone className="w-4 h-4 mr-1" /> Log Call
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => { setReminderDate(""); setReminderNote(""); setIsReminderOpen(true); }}
            disabled={!selectedRowId || selectedRowId.startsWith("empty-")}
            data-testid="button-set-reminder"
          >
            <Bell className="w-4 h-4 mr-1" /> Set Reminder
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => setIsActivityOpen(true)}
            disabled={!selectedRowId || selectedRowId.startsWith("empty-")}
            data-testid="button-activity"
          >
            <Eye className="w-4 h-4 mr-1" /> Activity
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!selectedRowId || selectedRowId.startsWith("empty-")} data-testid="dropdown-prospect-actions">
                Prospect Actions <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => { if (selectedRowId) convertMutation.mutate(selectedRowId); }}
                data-testid="action-convert"
              >
                <Calendar className="w-4 h-4 mr-2 text-primary" /> Convert
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { if (selectedRowId) updateMutation.mutate({ id: selectedRowId, updates: { status: "interested" } }); }}
                data-testid="action-interested"
              >
                <Star className="w-4 h-4 mr-2 text-yellow-500" /> Interested
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { if (selectedRowId) updateMutation.mutate({ id: selectedRowId, updates: { status: "not_now" } }); }}
                data-testid="action-not-now"
              >
                <Clock className="w-4 h-4 mr-2 text-orange-500" /> Not Now
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => { if (selectedRowId) updateMutation.mutate({ id: selectedRowId, updates: { status: "disqualified" } }); }}
                data-testid="action-disqualify"
              >
                <XCircle className="w-4 h-4 mr-2" /> Disqualify
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { if (selectedRowId) updateMutation.mutate({ id: selectedRowId, updates: { status: "new" } }); }}
                data-testid="action-clear-status"
              >
                <RotateCcw className="w-4 h-4 mr-2 text-muted-foreground" /> Clear
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => selectedRowId && setConfirmDeleteId(selectedRowId)}
                data-testid="action-delete-prospect"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            size="sm"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            data-testid="button-add-prospect"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Prospect
          </Button>
        </div>
      </div>

      {/* ── Bulk selection bar ────────────────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-primary/10 border-b border-border flex-wrap">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <Button variant="outline" size="sm" onClick={() => setIsBulkEditOpen(true)} data-testid="button-bulk-edit-prospects">
            <Edit className="w-4 h-4 mr-1" /> Bulk Edit
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setIsBulkDeleteOpen(true)} data-testid="button-bulk-delete-prospects">
            <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedIds.size}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Clear</Button>
        </div>
      )}

      {/* ── Spreadsheet ───────────────────────────────────────────────────────── */}
      <div ref={scrollRef} className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
            <p className="text-sm font-medium">Failed to load prospects.</p>
            <button className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => refetch()}>Retry</button>
          </div>
        ) : (
          <div className="min-w-max">
            {/* Header */}
            <div className="flex sticky top-0 bg-muted border-b border-border z-10">
              <div className="w-8 shrink-0 border-r border-border flex items-center justify-center">
                <Checkbox
                  checked={sortedProspects.length > 0 && sortedProspects.every((p) => selectedIds.has(p.id))}
                  onCheckedChange={(checked) => {
                    if (checked) setSelectedIds(new Set(sortedProspects.map((p) => p.id)));
                    else setSelectedIds(new Set());
                  }}
                  data-testid="checkbox-select-all-prospects"
                />
              </div>
              {COLUMNS.map((col) => (
                <div
                  key={col.key}
                  draggable
                  onDragStart={(e) => { setDraggedColKey(col.key); e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => {
                    if (!draggedColKey || draggedColKey === col.key) return;
                    e.preventDefault();
                    if (dragOverColKey !== col.key) setDragOverColKey(col.key);
                  }}
                  onDragLeave={() => { if (dragOverColKey === col.key) setDragOverColKey(null); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = draggedColKey || e.dataTransfer.getData("text/plain");
                    if (src && src !== col.key) moveColumn(src, col.key);
                    setDraggedColKey(null); setDragOverColKey(null);
                  }}
                  onDragEnd={() => { setDraggedColKey(null); setDragOverColKey(null); }}
                  className={cn(
                    "spreadsheet-cell font-medium text-sm bg-muted relative group cursor-grab active:cursor-grabbing",
                    draggedColKey === col.key && "opacity-40",
                    dragOverColKey === col.key && draggedColKey !== col.key && "border-l-2 border-l-primary",
                  )}
                  style={{ width: getColumnWidth(col.key), minWidth: 50 }}
                  data-testid={`column-header-${col.key}`}
                >
                  <button onClick={() => toggleSort(col.key)} className="flex items-center hover:text-foreground w-full">
                    {col.label}
                    <SortIcon field={col.key} />
                  </button>
                  <div
                    className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50 group-hover:bg-border"
                    onMouseDown={(e) => handleResizeStart(e, col.key)}
                  />
                </div>
              ))}
            </div>

            {/* Rows (virtualized) */}
            <div style={{ height: rowVirtualizer.getTotalSize(), width: "100%", position: "relative" }}>
              {rowVirtualizer.getVirtualItems().map((vRow) => {
                const prospect = sortedProspects[vRow.index];
                if (!prospect) return null;
                return (
                  <div
                    key={prospect.id}
                    data-index={vRow.index}
                    className={cn(
                      "flex border-b border-border hover:bg-muted/30 transition-colors",
                      getRowClassName(prospect),
                      selectedIds.has(prospect.id) && "bg-primary/10",
                    )}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${vRow.start}px)` }}
                  >
                    <div className="w-8 shrink-0 h-7 flex items-center justify-center border-r border-border bg-muted/50">
                      <Checkbox
                        checked={selectedIds.has(prospect.id)}
                        onCheckedChange={(checked) => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            checked ? next.add(prospect.id) : next.delete(prospect.id);
                            return next;
                          });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`checkbox-prospect-${prospect.id}`}
                      />
                    </div>
                    {COLUMNS.map((col) => (
                      <div key={col.key} style={{ width: getColumnWidth(col.key), minWidth: 50 }}>
                        {col.type === "region" ? (
                          <Select
                            value={prospect.region ?? ""}
                            onValueChange={(v) => handleCellChange(prospect.id, "region", v)}
                          >
                            <SelectTrigger className="h-7 border-0 rounded-none text-sm focus:ring-0 focus:ring-offset-0">
                              <SelectValue placeholder="Select region" />
                            </SelectTrigger>
                            <SelectContent>
                              {regions.map((r) => (
                                <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : col.type === "select" && col.options ? (
                          <Select
                            value={(prospect as any)[col.key] || ""}
                            onValueChange={(v) => handleCellChange(prospect.id, col.key, v)}
                          >
                            <SelectTrigger className="h-7 border-0 rounded-none text-sm focus:ring-0 focus:ring-offset-0">
                              <SelectValue placeholder={`Select ${col.label}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {col.options.map((opt: string) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : col.type === "checkbox" ? (
                          <div className="spreadsheet-cell h-7 flex items-center justify-center border-r border-border">
                            <Checkbox
                              checked={(prospect as any)[col.key] || false}
                              onCheckedChange={(v) => handleCellChange(prospect.id, col.key, v === true)}
                              data-testid={`checkbox-${col.key}-${prospect.id}`}
                            />
                          </div>
                        ) : col.type === "starred" ? (
                          <div className="spreadsheet-cell h-7 flex items-center justify-center border-r border-border">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCellChange(prospect.id, "starred", !prospect.starred);
                              }}
                              className="p-0.5 rounded hover:bg-muted transition-colors"
                              data-testid={`button-star-prospect-${prospect.id}`}
                              title={prospect.starred ? "Unstar" : "Star"}
                            >
                              <Star
                                className={cn(
                                  "w-4 h-4 transition-colors",
                                  prospect.starred
                                    ? "fill-amber-400 text-amber-400"
                                    : "text-muted-foreground/30 hover:text-amber-300",
                                )}
                              />
                            </button>
                          </div>
                        ) : col.key === "notes" && !(editingCell?.rowId === prospect.id && editingCell?.column === "notes") ? (
                          <div
                            className="spreadsheet-cell h-7 flex items-center border-r border-border px-2 cursor-text overflow-hidden"
                            onDoubleClick={(e) => {
                              e.preventDefault();
                              setSelectedRowId(prospect.id);
                              setEditingCell(null);
                              setNotesProspectId(prospect.id);
                              setNotesValue(prospect.notes ?? "");
                              setIsNotesOpen(true);
                            }}
                            onClick={() => { setSelectedRowId(prospect.id); setEditingCell({ rowId: prospect.id, column: "notes" }); }}
                            title="Double-click to open full editor"
                            data-testid={`cell-notes-${prospect.id}`}
                          >
                            <span className={cn("text-sm truncate w-full", !prospect.notes && "text-muted-foreground/50")}>
                              {prospect.notes || "Add notes…"}
                            </span>
                          </div>
                        ) : col.key === "website" && !(editingCell?.rowId === prospect.id && editingCell?.column === "website") ? (
                          (() => {
                            const raw = (prospect.website ?? "").trim();
                            const href = raw && !/^https?:\/\//i.test(raw) ? `https://${raw}` : raw;
                            return (
                              <div className="spreadsheet-cell h-7 flex items-center gap-1 border-r border-border px-2 group">
                                {raw ? (
                                  <>
                                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                      <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                    <button type="button" className="flex-1 min-w-0 text-left text-sm truncate text-muted-foreground hover:text-foreground"
                                      onClick={() => { setSelectedRowId(prospect.id); setEditingCell({ rowId: prospect.id, column: "website" }); }}>
                                      {raw}
                                    </button>
                                  </>
                                ) : (
                                  <button type="button" className="w-full h-full text-left text-sm text-muted-foreground/50 hover:text-foreground"
                                    onClick={() => { setSelectedRowId(prospect.id); setEditingCell({ rowId: prospect.id, column: "website" }); }}>
                                    Add website…
                                  </button>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <SpreadsheetCell
                            value={(prospect as any)[col.key] || ""}
                            onChange={(v) => handleCellChange(prospect.id, col.key, v)}
                            isEditing={editingCell?.rowId === prospect.id && editingCell?.column === col.key}
                            onStartEdit={() => { setSelectedRowId(prospect.id); setEditingCell({ rowId: prospect.id, column: col.key }); }}
                            onEndEdit={() => setEditingCell(null)}
                            onTab={(shift) => handleTabNavigation(prospect.id, col.key, shift)}
                            onFocus={() => setSelectedRowId(prospect.id)}
                            type={col.type as any}
                            placeholder={col.label}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Empty rows */}
            {emptyRows.map((row, idx) => {
              const isFirst = idx === 0;
              return (
                <div key={row.id} className={cn("flex border-b border-border transition-opacity", isFirst ? "opacity-60 hover:opacity-100" : "opacity-30 pointer-events-none")}>
                  <div className="w-8 shrink-0 h-7 border-r border-border bg-muted/50 flex items-center justify-center text-xs text-muted-foreground">
                    {sortedProspects.length + idx + 1}
                  </div>
                  {COLUMNS.map((col, colIdx) => (
                    <div key={col.key} style={{ width: getColumnWidth(col.key), minWidth: 50 }}>
                      {!isFirst || col.type === "region" || col.type === "select" || col.type === "checkbox" || col.type === "starred" ? (
                        <div className="h-7 border-r border-border" />
                      ) : (
                        <SpreadsheetCell
                          value=""
                          onChange={(v) => { if (v) createMutation.mutate(); }}
                          isEditing={editingCell?.rowId === row.id && editingCell?.column === col.key}
                          onStartEdit={() => setEditingCell({ rowId: row.id, column: col.key })}
                          onEndEdit={() => setEditingCell(null)}
                          type={col.type as any}
                          placeholder={colIdx === 0 ? "Click to add prospect…" : ""}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Add more rows */}
            <div className="flex items-center justify-center py-1 border-b border-border bg-muted/30">
              <Button variant="ghost" size="sm" onClick={() => setVisibleRowCount((n) => n + 10)} className="text-muted-foreground" data-testid="button-add-more-rows">
                + Add 10 rows
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Dialogs ───────────────────────────────────────────────────────────── */}

      {/* Email */}
      <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle>Send Email to {selectedProspect?.contactName || selectedProspect?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>To</Label>
              <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="input-email-to" />
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} data-testid="input-email-subject" />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <VoiceToTextTextarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={6} data-testid="input-email-body" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEmailOpen(false)}>Cancel</Button>
            <Button
              variant="outline"
              disabled={!emailTo || !emailSubject || !emailBody}
              onClick={() => setIsProspectEmailScheduleOpen(true)}
              data-testid="button-schedule-prospect-email"
            >
              <Clock className="w-4 h-4 mr-2" /> Schedule send
            </Button>
            <Button
              disabled={!emailTo || !emailSubject || !emailBody}
              onClick={() => {
                if (selectedRowId) {
                  activityMutation.mutate({ id: selectedRowId, type: "email", notes: `To: ${emailTo}\nSubject: ${emailSubject}\n\n${emailBody}` });
                }
                setIsEmailOpen(false);
              }}
              data-testid="button-send-email"
            >
              <Send className="w-4 h-4 mr-2" /> Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduledEmailDialog
        open={isProspectEmailScheduleOpen}
        onOpenChange={setIsProspectEmailScheduleOpen}
        sourceType="one_off"
        recipientSummary={emailTo ? `Schedule email to ${emailTo}` : undefined}
        subjectSummary={emailSubject}
        buildPayload={() => {
          if (!emailTo || !emailSubject || !emailBody) return null;
          return {
            to: emailTo,
            subject: emailSubject,
            htmlBody: emailBody,
            prospectId: selectedRowId,
          };
        }}
        onScheduled={() => setIsEmailOpen(false)}
      />

      {/* Log Call */}
      <LogCallDialog
        open={isLogCallOpen}
        onOpenChange={setIsLogCallOpen}
        title={`Log Call for ${selectedProspect?.contactName || selectedProspect?.name || "Prospect"}`}
        onSave={(notes) => { if (selectedRowId) activityMutation.mutate({ id: selectedRowId, type: "call", notes }); }}
        isPending={activityMutation.isPending}
      />

      {/* Notes full editor */}
      <Dialog open={isNotesOpen} onOpenChange={(open) => { if (!open) { setIsNotesOpen(false); setNotesProspectId(null); setNotesValue(""); } }}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Notes — {prospects.find((p) => p.id === notesProspectId)?.name || "Prospect"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <VoiceToTextTextarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              placeholder="Add notes…"
              rows={14}
              className="resize-y min-h-[260px] font-mono text-sm"
              data-testid="textarea-edit-notes"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setIsNotesOpen(false); setNotesProspectId(null); setNotesValue(""); }}>Cancel</Button>
              <Button onClick={() => {
                if (notesProspectId) { handleCellChange(notesProspectId, "notes", notesValue); }
                setIsNotesOpen(false); setNotesProspectId(null); setNotesValue("");
              }} data-testid="button-save-notes">Save Notes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Reminder */}
      <Dialog open={isReminderOpen} onOpenChange={setIsReminderOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Set Reminder for {selectedProspect?.contactName || selectedProspect?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} data-testid="input-reminder-date" />
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <VoiceToTextTextarea value={reminderNote} onChange={(e) => setReminderNote(e.target.value)} rows={3} placeholder="Add a note…" data-testid="input-reminder-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReminderOpen(false)}>Cancel</Button>
            <Button
              disabled={!reminderDate}
              onClick={() => {
                if (selectedRowId && reminderDate) {
                  activityMutation.mutate({ id: selectedRowId, type: "note", notes: `[Reminder: ${reminderDate}] ${reminderNote}`.trim() });
                }
                setIsReminderOpen(false);
              }}
              data-testid="button-save-reminder"
            >
              Save Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity timeline */}
      <Dialog open={isActivityOpen} onOpenChange={setIsActivityOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Activity — {selectedProspect?.contactName || selectedProspect?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedProspect && (
              <div className="p-4 bg-muted rounded-lg space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">Account:</span> {selectedProspect.name}</div>
                  <div><span className="text-muted-foreground">Contact:</span> {selectedProspect.contactName || "N/A"}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {selectedProspect.phone || "N/A"}</div>
                  <div><span className="text-muted-foreground">Email:</span> {selectedProspect.email}</div>
                  <div><span className="text-muted-foreground">Region:</span> {selectedProspect.region || "N/A"}</div>
                  <div><span className="text-muted-foreground">Status:</span> {selectedProspect.status}</div>
                </div>
              </div>
            )}
            <div>
              <h4 className="font-medium mb-3">Activity History</h4>
              {activities.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity yet.</p>
              ) : (
                <div className="space-y-3">
                  {activities.map((act) => (
                    <div key={act.id} className="flex gap-3 p-3 border rounded-lg">
                      <div className="flex-shrink-0">
                        {act.type === "call" && <Phone className="w-5 h-5 text-blue-500" />}
                        {act.type === "email" && <Mail className="w-5 h-5 text-green-500" />}
                        {act.type === "note" && <Pencil className="w-5 h-5 text-orange-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <span className="font-medium capitalize text-sm">{act.type}</span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(act.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        {act.notes && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{act.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDeleteId} onOpenChange={(open) => { if (!open) setConfirmDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this prospect?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (confirmDeleteId) handleDeleteWithUndo(confirmDeleteId); setConfirmDeleteId(null); }}
              data-testid="button-confirm-delete-prospect"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete */}
      <Dialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete {selectedIds.size} Prospects?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-4">This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsBulkDeleteOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => bulkDeleteMutation.mutate([...selectedIds])}
              disabled={bulkDeleteMutation.isPending}
            >
              {bulkDeleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete {selectedIds.size}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk edit */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk Edit {selectedIds.size} Prospects</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Field</Label>
              <Select value={bulkEditField} onValueChange={setBulkEditField}>
                <SelectTrigger data-testid="select-bulk-edit-field"><SelectValue placeholder="Select field…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="region">Region</SelectItem>
                  <SelectItem value="businessType">Business Type</SelectItem>
                  <SelectItem value="rep">Rep</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bulkEditField && (
              <div className="space-y-2">
                <Label>Value</Label>
                {bulkEditField === "region" ? (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                    <SelectTrigger><SelectValue placeholder="Select region…" /></SelectTrigger>
                    <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : bulkEditField === "businessType" ? (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                    <SelectTrigger><SelectValue placeholder="Select type…" /></SelectTrigger>
                    <SelectContent>{businessTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                ) : bulkEditField === "status" ? (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                    <SelectTrigger><SelectValue placeholder="Select status…" /></SelectTrigger>
                    <SelectContent>
                      {["new", "contacted", "qualified", "converted", "lost"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={bulkEditValue} onChange={(e) => setBulkEditValue(e.target.value)} placeholder="Enter value…" />
                )}
              </div>
            )}
            <Button
              className="w-full"
              disabled={!bulkEditField || !bulkEditValue || bulkEditMutation.isPending}
              onClick={() => bulkEditMutation.mutate({ ids: [...selectedIds], fields: { [bulkEditField]: bulkEditValue } })}
            >
              {bulkEditMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Apply to {selectedIds.size} Prospects
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Region delete with reassignment */}
      <AlertDialog open={!!regionDeleteError} onOpenChange={(open) => { if (!open) { setRegionDeleteError(null); setReassignTargetRegionId(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Delete "{regionDeleteError?.regionName}"</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>{regionDeleteError?.message}</p>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">Reassign prospects to:</Label>
                  <Select value={reassignTargetRegionId} onValueChange={setReassignTargetRegionId}>
                    <SelectTrigger data-testid="select-reassign-target"><SelectValue placeholder="Select region…" /></SelectTrigger>
                    <SelectContent>
                      {regions.filter((r) => r.id !== regionDeleteError?.regionId).map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={!reassignTargetRegionId || deleteRegionWithReassignMutation.isPending}
              onClick={() => {
                if (regionDeleteError?.regionId && reassignTargetRegionId) {
                  deleteRegionWithReassignMutation.mutate({ regionId: regionDeleteError.regionId, reassignToRegionId: reassignTargetRegionId });
                }
              }}
            >
              {deleteRegionWithReassignMutation.isPending ? "Reassigning…" : "Delete & Reassign"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
